/**
 * useDrawerTabs - Hook for building drawer tab configurations
 *
 * This hook provides a composable way to build tab configurations for drawers.
 * Instead of manually building TabItem arrays, you can use predefined tab types
 * and let the hook handle the component mapping and prop merging.
 *
 * @example
 * ```tsx
 * const { buildTabs } = useDrawerTabs()
 *
 * const tabs = buildTabs({
 *   common: { deal, organization, expanded },
 *   tabs: [
 *     { type: 'onboarding', props: { tasklists, checklistProgress, onChange } },
 *     { type: 'commandCenter', props: { tasks: ncoTasks } },
 *     { type: 'comments', props: { taskSKs, context: 'nco', contextFilters: ['nco'] } },
 *   ]
 * })
 * ```
 */

import { useCallback } from 'react'
import type { TabItem } from 'types'
import { EMPTY_FORM_DATA, TAB_REGISTRY } from './tabRegistry'
import type {
  BuildDrawerTabsOptions,
  CommandCenterTabProps,
  CommentsTabProps,
  CommonTabProps,
  FinancialTotalsProps,
  IntegrationTabProps,
  KickoffCallTabProps,
  MonthlyTasksTabProps,
  OnboardingTabProps,
  OptimizationsTabProps,
  ReviewDealTabProps,
  TabType,
} from './types'

// Import components directly to avoid async issues
import FinancialTotals from 'components/Accounting/FinancialTotals'
import CommentsInput from 'components/Comments/CommentsInput'
import CommandCenterTasks from 'components/Creative/CommandCenterTasks'
import IntegrationChecklistTable from 'components/DataGrid/components/IntegrationChecklistTable'
import KickoffCall from 'components/DataGrid/components/KickoffCall'
import ReviewDeal from 'components/DealApproval/ReviewDeal'
import OptimizationsContainer from 'components/Optimizations/OptimizationsContainer'
import OrgDealInfo from 'components/Organizations/OrgDealInfo'
import RenderCampaign from 'components/StrategyCampaigns/RenderCampaign'

/**
 * Build props for a specific tab type
 */
function buildTabProps(type: TabType, tabProps: any, common: CommonTabProps): Record<string, any> {
  const { deal, organization, expanded } = common

  switch (type) {
    case 'onboarding': {
      const props = tabProps as OnboardingTabProps
      return {
        deal,
        organization,
        checklists: props.tasklists,
        checklistType: 'Onboarding',
        checklistProgress: props.checklistProgress,
        expanded: expanded ?? false,
        onChange: props.onChange,
        currentChecklistOverride: props.currentChecklist ?? null,
        seededRowsOverride: props.seededRows ?? null,
        completedSetOverride: props.completedSet ?? null,
        progressOverride: props.progress ?? null,
      }
    }

    case 'commandCenter': {
      const props = tabProps as CommandCenterTabProps
      return {
        tasks: props.tasks,
        expanded,
      }
    }

    case 'comments': {
      const props = tabProps as CommentsTabProps
      return {
        taskSKs: props.taskSKs,
        contextFilters: props.contextFilters,
        expanded: !!expanded,
        context: props.context,
        checklistItems: props.checklistItems,
      }
    }

    case 'reviewDeal': {
      const props = tabProps as ReviewDealTabProps
      return {
        moduleLocation: props.moduleLocation ?? 'DrawerLayout',
        isHeaderExpanded: !!expanded,
      }
    }

    case 'kickoffCall': {
      const props = tabProps as KickoffCallTabProps
      return {
        organization,
        deal,
        onCreateDoc: props.onCreateDoc,
        expanded,
        isCreating: props.isCreating,
      }
    }

    case 'integration': {
      const props = tabProps as IntegrationTabProps
      return {
        deal,
        organization,
        formData: props.fields,
        setFormData: props.setFields,
        checklistData: props.checklist ?? [],
        expanded: !!expanded,
        onMirrorChecklists: props.onMirrorChecklists,
        onSaved: props.onSaved,
        type: 'integration',
      }
    }

    case 'seo': {
      const props = tabProps as IntegrationTabProps
      return {
        deal,
        organization,
        formData: EMPTY_FORM_DATA,
        setFormData: undefined,
        checklistData: props.checklist ?? [],
        expanded: !!expanded,
        onMirrorChecklists: props.onMirrorChecklists,
        onSaved: props.onSaved,
        type: 'seo',
      }
    }

    case 'optimizations': {
      const props = tabProps as OptimizationsTabProps
      return {
        organization,
        expanded,
        context: props.context,
        monthKey: props.monthKey,
        onExpandedIdChange: props.onExpandedIdChange,
      }
    }

    case 'monthlyTasksSeo':
    case 'monthlyTasksAm':
    case 'monthlyTasksDms': {
      const props = tabProps as MonthlyTasksTabProps
      return {
        optimizations: props.optimizations,
        assignees: props.assignees,
        monthlyTasks: props.monthlyTasks,
        onMonthlyTaskChange: props.onMonthlyTaskChange,
        onUpdateStatus: props.onUpdateStatus,
        onUpdateActiveAssignee: props.onUpdateActiveAssignee,
        expanded,
      }
    }

    case 'renderCampaign':
      return {}

    case 'dealsInfo':
      return {}

    case 'financial': {
      const props = tabProps as FinancialTotalsProps
      return {
        deal: props.deal,
        productRows: props.productRows,
        containerHeight: props.containerHeight,
        hideViewIcon: props.hideViewIcon,
      }
    }

    default:
      return {}
  }
}

/**
 * Get the component for a tab type
 * Components that need to be imported dynamically should be passed in
 */
function getTabComponent(
  type: TabType,
  componentOverrides?: Partial<Record<TabType, React.ComponentType<any>>>
): React.ComponentType<any> {
  // Check for override first
  if (componentOverrides?.[type]) {
    return componentOverrides[type]!
  }

  // Return from static imports
  switch (type) {
    case 'commandCenter':
      return CommandCenterTasks
    case 'comments':
      return CommentsInput
    case 'reviewDeal':
      return ReviewDeal
    case 'kickoffCall':
      return KickoffCall
    case 'integration':
    case 'seo':
      return IntegrationChecklistTable
    case 'optimizations':
      return OptimizationsContainer
    case 'renderCampaign':
      return RenderCampaign
    case 'dealsInfo':
      return OrgDealInfo
    case 'financial':
      return FinancialTotals
    default:
      // For onboarding and monthly tasks, require component override
      throw new Error(`Tab type "${type}" requires a component override`)
  }
}

export type UseDrawerTabsOptions = {
  /**
   * Component overrides for tabs that need dynamic imports
   * (e.g., OnboardingTasks, RenderMonthlyTaskList)
   */
  components?: Partial<Record<TabType, React.ComponentType<any>>>
}

/**
 * Hook for building drawer tab configurations
 */
export function useDrawerTabs(options: UseDrawerTabsOptions = {}) {
  const { components } = options

  /**
   * Build a TabItem array from a declarative config
   */
  const buildTabs = useCallback(
    (config: BuildDrawerTabsOptions): TabItem[] => {
      const { common, tabs } = config

      return tabs.map(tabConfig => {
        const { type, props: tabProps, overrides } = tabConfig
        const registry = TAB_REGISTRY[type]

        // Get component (with potential override)
        const Component = getTabComponent(type, components)

        // Build props for this tab
        const props = buildTabProps(type, tabProps, common)

        return {
          label: overrides?.label ?? registry.label,
          display: overrides?.display ?? registry.display,
          Component,
          props,
          needsEditing: overrides?.needsEditing ?? registry.needsEditing ?? false,
          saveKind: overrides?.saveKind ?? registry.saveKind,
        }
      })
    },
    [components]
  )

  return { buildTabs }
}

export default useDrawerTabs
